import { useEffect, useRef } from 'react'
import type { ElementResizeHandler } from './useElementResize.types'

/**
 * Runs onResize whenever target changes size, coalesced to one animation frame.
 * The handler lives in a ref, so an inline arrow never rebuilds the observer.
 */
export function useElementResize(
  target: Element | null | undefined,
  onResize: ElementResizeHandler
): void {
  const handlerRef = useRef(onResize)

  useEffect(() => {
    handlerRef.current = onResize
  }, [onResize])

  useEffect(() => {
    if (!target || typeof ResizeObserver === 'undefined') return

    let frame: number | null = null

    function run() {
      frame = null

      handlerRef.current()
    }

    function schedule() {
      if (frame !== null) return

      frame = requestAnimationFrame(run)
    }

    const observer = new ResizeObserver(schedule)

    observer.observe(target)

    return () => {
      observer.disconnect()

      if (frame !== null) cancelAnimationFrame(frame)
    }
  }, [target])
}
